import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/utils/supabase";
import { redirectToExternalUrl } from "@/utils/externalRedirect";

const WECHAT_WORK_REPORT_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/wechat-work-report`;
const KUAISHOU_REPORT_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/kuaishou-report`;

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [orderStatus, setOrderStatus] = useState<"loading" | "paid" | "pending" | "error">("loading");
  const [reportStatus, setReportStatus] = useState<"idle" | "reporting" | "success" | "error">("idle");
  const [kuaishouStatus, setKuaishouStatus] = useState<"idle" | "reporting" | "success" | "error">("idle");
  const hasRedirectedRef = useRef(false);

  // 从 URL 参数获取订单号，企业微信链接从 localStorage 或订单数据获取
  const orderNo = searchParams.get("order_no") || "";
  const [wechatLink, setWechatLink] = useState(
    searchParams.get("wechat_link") || localStorage.getItem("wechat_link") || "",
  );
  
  // 企业微信回调URL（用于接收获客助手回调）
  const callbackUrl = `${window.location.origin}/wechat-callback?order_no=${orderNo}`;

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  // 查询订单支付状态
  useEffect(() => {
    if (!orderNo) {
      setOrderStatus("error");
      return;
    }

    const checkOrderStatus = async () => {
      try {
        const { data, error } = await supabase
          .from("orders")
          .select("status, wechat_link, name, amount")
          .eq("order_no", orderNo)
          .maybeSingle();

        if (error) {
          console.error("查询订单失败:", error);
          setOrderStatus("error");
          return;
        }

        if (data?.status === "paid") {
          setOrderStatus("paid");
          // 从订单中获取企业微信链接
          if (data?.wechat_link) {
            setWechatLink(data.wechat_link);
            localStorage.setItem("wechat_link", data.wechat_link);
          }
          
          // 上报企业微信转化事件（成交）
          reportConversionEvent(data.name, data.amount);
          
          // 上报快手广告转化事件
          reportKuaishouConversion(data.amount);
        } else {
          setOrderStatus("pending");
        }
      } catch (err) {
        console.error("查询异常:", err);
        setOrderStatus("error");
      }
    };

    checkOrderStatus();
    // 每3秒轮询一次
    const interval = setInterval(checkOrderStatus, 3000);
    return () => clearInterval(interval);
  }, [orderNo]);

  // 上报快手广告转化事件
  const reportKuaishouConversion = async (amount: number) => {
    if (kuaishouStatus !== "idle") return;
    
    // 从 URL 获取快手 callback（用户从广告点击时携带）
    const kuaishouCallback = searchParams.get("kuaishou_callback") || localStorage.getItem("kuaishou_callback") || "";
    
    if (!kuaishouCallback) {
      console.log("[SuccessPage] 未检测到快手广告来源，跳过回传");
      return;
    }
    
    setKuaishouStatus("reporting");
    console.log("[SuccessPage] 开始上报快手广告转化事件...");
    
    try {
      const res = await fetch(KUAISHOU_REPORT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          order_no: orderNo,
          callback: kuaishouCallback,
          event_type: "PURCHASE",
          amount: amount,
        }),
      });
      
      const result = await res.json();
      
      if (result.success) {
        console.log("[SuccessPage] 快手广告转化回传成功");
        setKuaishouStatus("success");
      } else {
        console.error("[SuccessPage] 快手广告转化回传失败:", result.error);
        setKuaishouStatus("error");
      }
    } catch (err) {
      console.error("[SuccessPage] 快手回传异常:", err);
      setKuaishouStatus("error");
    }
  };

  // 上报企业微信获客助手转化事件
  const reportConversionEvent = async (customerName: string, amount: number) => {
    if (reportStatus !== "idle") return;
    
    setReportStatus("reporting");
    console.log("[SuccessPage] 开始上报企业微信转化事件...");
    
    try {
      // 从 wechat_link 中提取 chat_id（如果链接格式包含）
      let chatId = "";
      if (wechatLink) {
        // 尝试从获客链接中提取 chat_id
        const match = wechatLink.match(/[?&]c=(\w+)/) || wechatLink.match(/\/ca\/(\w+)/);
        if (match) chatId = match[1];
      }
      
      const res = await fetch(WECHAT_WORK_REPORT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          chat_id: chatId,
          external_userid: "", // 用户添加客服后才能获取
          follow_userid: "",   // 用户添加客服后才能获取
          event_type: "purchase", // 成交
          event_msg: `订单${orderNo}成交，金额¥${amount}，客户：${customerName || "未知"}`,
        }),
      });
      
      const result = await res.json();
      
      if (result.success) {
        console.log("[SuccessPage] 企业微信转化事件上报成功");
        setReportStatus("success");
      } else {
        console.error("[SuccessPage] 企业微信转化事件上报失败:", result.error);
        setReportStatus("error");
      }
    } catch (err) {
      console.error("[SuccessPage] 上报异常:", err);
      setReportStatus("error");
    }
  };

  // 倒计时自动跳转到企业微信
  useEffect(() => {
    if (!wechatLink || orderStatus !== "paid" || hasRedirectedRef.current) return;

    // 支付成功后立刻跳转到企业微信，不等待
    hasRedirectedRef.current = true;
    redirectToExternalUrl(wechatLink);
  }, [wechatLink, orderStatus]);

  // 手动跳转按钮
  const handleJumpNow = () => {
    if (wechatLink) {
      hasRedirectedRef.current = true;
      redirectToExternalUrl(wechatLink);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{
        background: "linear-gradient(160deg, #0d0000 0%, #3a0808 25%, #5c1212 55%, #2e0000 100%)",
        fontFamily: "'Noto Serif SC', serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto Serif+SC:wght@400;600;700&display=swap');
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes ringPulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.15); }
        }
        @keyframes floatDot {
          0%, 100% { opacity: 0.15; transform: translateY(0); }
          50% { opacity: 0.35; transform: translateY(-8px); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .anim-fadeup { animation: fadeUp 0.7s ease both; }
      `}</style>

      {/* 背景粒子 */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #c8922a 0%, transparent 70%)" }} />
        {[...Array(10)].map((_, i) => (
          <div key={i} className="absolute rounded-full"
            style={{
              width: 4 + (i % 4),
              height: 4 + (i % 4),
              background: "radial-gradient(circle, #e8b84b, #c8922a)",
              left: `${(i * 9.7 + 5) % 100}%`,
              top: `${(i * 13.3 + 8) % 100}%`,
              opacity: 0.18,
              animation: `floatDot ${2.5 + (i % 3) * 0.7}s ${i * 0.3}s ease-in-out infinite`,
            }}
          />
        ))}
      </div>

      {/* 主卡片 */}
      <div
        className="relative w-full max-w-sm rounded-2xl overflow-hidden anim-fadeup"
        style={{
          background: "linear-gradient(135deg, #fdf3e3 0%, #f5e6c8 50%, #ede0b8 100%)",
          border: "3px solid #c8922a",
          boxShadow: "0 0 0 6px rgba(200,146,42,0.12), 0 20px 60px rgba(0,0,0,0.4)",
        }}
      >
        {/* 顶部条纹 */}
        <div className="h-3 w-full" style={{ background: "repeating-linear-gradient(90deg, #c8922a 0px, #c8922a 10px, #e8b84b 10px, #e8b84b 20px)" }} />

        <div className="px-8 py-10 text-center">
          {/* 成功图标 */}
          <div className="relative flex items-center justify-center mb-6">
            <div className="absolute w-24 h-24 rounded-full" style={{ border: "2px solid rgba(200,146,42,0.4)", animation: "ringPulse 2.5s ease-in-out infinite" }} />
            <div className="absolute w-32 h-32 rounded-full" style={{ border: "1px solid rgba(200,146,42,0.2)", animation: "ringPulse 2.5s 0.8s ease-in-out infinite" }} />
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #c8922a, #e8b84b)", boxShadow: "0 0 30px rgba(200,146,42,0.5)" }}
            >
              <i className="ri-check-line text-4xl" style={{ color: "#fff" }} />
            </div>
          </div>

          {/* 标题 */}
          <h1 className="text-2xl font-bold mb-2" style={{ color: "#8b1a1a" }}>支付成功</h1>
          <p className="text-sm mb-6" style={{ color: "#7a5c2e" }}>感谢您的信任，我们已收到您的测算申请</p>

          {/* 分隔线 */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1" style={{ background: "linear-gradient(to right, transparent, #c8922a)" }} />
            <span className="text-xs" style={{ color: "#c8922a" }}>✦</span>
            <div className="h-px flex-1" style={{ background: "linear-gradient(to left, transparent, #c8922a)" }} />
          </div>

          {/* 订单状态提示 */}
          {orderStatus === "loading" && (
            <div
              className="mb-5 px-4 py-3 rounded-xl"
              style={{ background: "rgba(200,146,42,0.1)", border: "1px solid rgba(200,146,42,0.3)" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ background: "rgba(200,146,42,0.15)" }}>
                  <i className="ri-loader-4-line" style={{ color: "#c8922a", fontSize: "20px", animation: "spin 1s linear infinite" }} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold" style={{ color: "#c8922a" }}>正在确认支付状态...</p>
                  <p className="text-xs" style={{ color: "#5a3a10" }}>请稍候，系统正在处理</p>
                </div>
              </div>
            </div>
          )}

          {orderStatus === "pending" && (
            <div
              className="mb-5 px-4 py-3 rounded-xl"
              style={{ background: "rgba(255,193,7,0.1)", border: "1px solid rgba(255,193,7,0.3)" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ background: "rgba(255,193,7,0.15)" }}>
                  <i className="ri-time-line" style={{ color: "#ffc107", fontSize: "20px" }} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold" style={{ color: "#b8860b" }}>支付处理中</p>
                  <p className="text-xs" style={{ color: "#5a3a10" }}>请稍候，系统正在确认支付结果</p>
                </div>
              </div>
            </div>
          )}

          {orderStatus === "paid" && wechatLink && (
            <>
              {/* 回调URL提示 */}
              <div
                className="mb-3 px-4 py-3 rounded-xl"
                style={{ background: "rgba(200,146,42,0.08)", border: "1px solid rgba(200,146,42,0.2)" }}
              >
                <p className="text-xs font-semibold mb-1" style={{ color: "#8b1a1a" }}>企业微信回调地址（请复制到获客链接配置）</p>
                <div 
                  className="text-xs break-all p-2 rounded-lg cursor-pointer"
                  style={{ background: "rgba(255,255,255,0.5)", color: "#5a3a10", fontFamily: "monospace" }}
                  onClick={() => {
                    navigator.clipboard.writeText(callbackUrl);
                    alert("回调地址已复制到剪贴板");
                  }}
                >
                  {callbackUrl}
                </div>
                <p className="text-xs mt-1" style={{ color: "#7a5c2e" }}>点击复制，粘贴到企业微信获客链接的回调URL配置中</p>
              </div>
              
              {/* 转化事件上报状态 */}
              {reportStatus === "reporting" && (
                <div
                  className="mb-3 px-4 py-2 rounded-xl"
                  style={{ background: "rgba(200,146,42,0.08)", border: "1px solid rgba(200,146,42,0.2)" }}
                >
                  <div className="flex items-center gap-2">
                    <i className="ri-loader-4-line text-xs" style={{ color: "#c8922a", animation: "spin 1s linear infinite" }} />
                    <span className="text-xs" style={{ color: "#7a5c2e" }}>正在同步到企业微信...</span>
                  </div>
                </div>
              )}
              {reportStatus === "success" && (
                <div
                  className="mb-3 px-4 py-2 rounded-xl"
                  style={{ background: "rgba(39,174,96,0.08)", border: "1px solid rgba(39,174,96,0.2)" }}
                >
                  <div className="flex items-center gap-2">
                    <i className="ri-check-line text-xs" style={{ color: "#27ae60" }} />
                    <span className="text-xs" style={{ color: "#27ae60" }}>已同步到企业微信（成交）</span>
                  </div>
                </div>
              )}
              {reportStatus === "error" && (
                <div
                  className="mb-3 px-4 py-2 rounded-xl"
                  style={{ background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.2)" }}
                >
                  <div className="flex items-center gap-2">
                    <i className="ri-error-warning-line text-xs" style={{ color: "#e74c3c" }} />
                    <span className="text-xs" style={{ color: "#e74c3c" }}>企业微信同步失败（不影响支付）</span>
                  </div>
                </div>
              )}

              {/* 快手广告回传状态 */}
              {kuaishouStatus === "reporting" && (
                <div
                  className="mb-3 px-4 py-2 rounded-xl"
                  style={{ background: "rgba(255,100,0,0.08)", border: "1px solid rgba(255,100,0,0.2)" }}
                >
                  <div className="flex items-center gap-2">
                    <i className="ri-loader-4-line text-xs" style={{ color: "#ff6400", animation: "spin 1s linear infinite" }} />
                    <span className="text-xs" style={{ color: "#ff6400" }}>正在同步到快手广告...</span>
                  </div>
                </div>
              )}
              {kuaishouStatus === "success" && (
                <div
                  className="mb-3 px-4 py-2 rounded-xl"
                  style={{ background: "rgba(255,100,0,0.08)", border: "1px solid rgba(255,100,0,0.2)" }}
                >
                  <div className="flex items-center gap-2">
                    <i className="ri-check-line text-xs" style={{ color: "#ff6400" }} />
                    <span className="text-xs" style={{ color: "#ff6400" }}>已同步到快手广告（成交）</span>
                  </div>
                </div>
              )}
              {kuaishouStatus === "error" && (
                <div
                  className="mb-3 px-4 py-2 rounded-xl"
                  style={{ background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.2)" }}
                >
                  <div className="flex items-center gap-2">
                    <i className="ri-error-warning-line text-xs" style={{ color: "#e74c3c" }} />
                    <span className="text-xs" style={{ color: "#e74c3c" }}>快手广告同步失败（不影响支付）</span>
                  </div>
                </div>
              )}
              
              <div
                className="mb-5 px-4 py-3 rounded-xl"
                style={{ background: "rgba(7,193,96,0.1)", border: "1px solid rgba(7,193,96,0.3)" }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ background: "rgba(7,193,96,0.15)" }}>
                    <i className="ri-wechat-fill" style={{ color: "#07c160", fontSize: "20px" }} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold" style={{ color: "#07c160" }}>即将跳转到企业微信</p>
                    <p className="text-xs" style={{ color: "#5a3a10" }}>正在跳转，请添加客服微信</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {orderStatus === "paid" && !wechatLink && (
            <div
              className="mb-5 px-4 py-3 rounded-xl"
              style={{ background: "rgba(7,193,96,0.1)", border: "1px solid rgba(7,193,96,0.3)" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ background: "rgba(7,193,96,0.15)" }}>
                  <i className="ri-check-line" style={{ color: "#07c160", fontSize: "20px" }} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold" style={{ color: "#07c160" }}>支付成功</p>
                  <p className="text-xs" style={{ color: "#5a3a10" }}>客服将尽快联系您</p>
                </div>
              </div>
            </div>
          )}

          {/* 提示信息 */}
          <div className="space-y-3 mb-8">
            {[
              { icon: "ri-time-line", text: "测算结果将在24小时内完成" },
              { icon: "ri-customer-service-line", text: "专属命理师正在为您分析" },
              { icon: "ri-shield-check-line", text: "结果将通过客服联系您" },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{ background: "rgba(200,146,42,0.08)", border: "1px solid rgba(200,146,42,0.2)" }}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ background: "rgba(200,146,42,0.15)" }}>
                  <i className={item.icon} style={{ color: "#c8922a", fontSize: "16px" }} />
                </div>
                <span className="text-sm text-left" style={{ color: "#5a3a10" }}>{item.text}</span>
              </div>
            ))}
          </div>

          {/* 立即跳转按钮 - 只在有企业微信链接且支付成功时显示 */}
          {orderStatus === "paid" && wechatLink && (
            <button
              onClick={handleJumpNow}
              className="block w-full py-3 rounded-xl text-base font-bold tracking-widest cursor-pointer whitespace-nowrap mb-3"
              style={{
                background: "linear-gradient(135deg, #07c160 0%, #10b981 50%, #07c160 100%)",
                color: "#fff",
                letterSpacing: "0.12em",
                boxShadow: "0 4px 20px rgba(7,193,96,0.35)",
                border: "none",
              }}
            >
              <i className="ri-wechat-fill mr-2" />
              立即添加客服微信
            </button>
          )}

          {/* 返回按钮 */}
          <a
            href="/"
            className="block w-full py-3 rounded-xl text-base font-bold tracking-widest cursor-pointer whitespace-nowrap"
            style={{
              background: "linear-gradient(135deg, #c0392b 0%, #e74c3c 50%, #c0392b 100%)",
              color: "#fff",
              letterSpacing: "0.12em",
              boxShadow: "0 4px 20px rgba(192,57,43,0.35)",
              textDecoration: "none",
            }}
          >
            返回首页
          </a>
        </div>

        {/* 底部条纹 */}
        <div className="h-3 w-full" style={{ background: "repeating-linear-gradient(90deg, #c8922a 0px, #c8922a 10px, #e8b84b 10px, #e8b84b 20px)" }} />
      </div>

      {/* 底部提示 */}
      <p
        className="mt-6 text-xs text-center max-w-xs anim-fadeup"
        style={{ color: "#5a3a10", animationDelay: "0.3s" }}
      >
        如有疑问请联系客服，我们将竭诚为您服务
      </p>
    </div>
  );
}
